<template>
  <div class="thematic-card-map">
    <div class="map-responsive-container">
      <div class="map-responsive">
        <div class="mapbox-container" :style="`height:${height}px; width:${width}%`" id="map-iframe"></div>
      </div>
    </div>
    <div v-if="!hideTextMap" class="text-in-area search-warning-message" role="alert">
      <p>{{ messageMap }}</p>
    </div>
  </div>
</template>
<script>

import {
  onMounted, watch, nextTick, inject,
} from 'vue';
import MapLeafletHook from '@/hooks/mapLeaflet.hook';
import { thematicCardService } from '@/services';
import resursiveCheckNoGroups from '@/helpers/leaflet.helper';

export default {
  emits: ['onClickCard', 'onClickCardIframe', 'onCloseCard', 'updateBoundingBox', 'update:popupOpen'],
  props: {
    modeType: { type: String, default: 'map' },
    cards: { type: Array, default: () => [] },
    whereValue: { type: String, default: '' },
    height: { type: String, default: '500' },
    width: { type: String, default: '100' },
    hideTextMap: { type: Boolean, default: true },
    messageMap: { type: String, default: '' },
    popupOpen: { type: Boolean, default: false },
    modeIframe: { type: Boolean, default: false },
    personalizedZoom: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    // Assuming leaflet is loaded
    const { L } = window;
    const { initMap } = MapLeafletHook();
    let mapSettings;
    let flyingOngoing = true;
    const isMobile = inject('isScreenMdMin');
    const isRestModalFilter = inject('isRestModalFilter');
    const markerAroundMe = inject('markerAroundMe');
    const personalizedZoom = inject('personalizedZoom');

    // Init marker cluster options
    const markerGroup = new L.MarkerClusterGroup({
      spiderfyOnMaxZoom: false,
      zoomToBoundsOnClick: false,
      showCoverageOnHover: false,
      iconCreateFunction(cluster) {
        // Different cluster style
        if (resursiveCheckNoGroups(mapSettings.map, cluster)) {
          return L.divIcon({
            html: `<span>${cluster.getChildCount()}</span>`,
            className: 'leaflet-marker-icon marker-cluster-position',
            iconSize: null,
          });
        }

        return L.divIcon({
          html: `<span class="sante-leaflet-marker"><span>${cluster.getChildCount()}</span></span>`,
          className: 'sante-leaflet-marker-cluster',
          iconSize: null,
        });
      },
    });

    function openPopup(latlng) {
      L.popup({
        offset: [0, 30],
        className: 'leaflet-popup--cluster',
      })
        .setLatLng(latlng)
        .setContent('<div id="sante-leaflet-popup-content"></div>')
        .on('remove', () => emit('onCloseCard'))
        .openOn(mapSettings.map);
    }

    function emitClickCard(markers, latlng, a) {
      if (!a.originalEvent.isTrusted) {
        return;
      }

      emit('update:popupOpen');

      const shouldOpenPopup = !isMobile.value;

      if (shouldOpenPopup) {
        openPopup(latlng);
      }

      mapSettings.map.panTo(latlng);

      nextTick(() => {
        if (props.modeIframe) {
          // emit only for iframe scope, send a.originalEvent causes issue
          emit('onClickCardIframe', { popup: shouldOpenPopup, items: markers.map((v) => v.options.nid) });
        } else {
          const markerOptions = markers[0].options;
          const propertiesToEmit = { popup: shouldOpenPopup, items: markers.map((v) => v.options.nid) };

          if (markerOptions.etb && markerOptions.os) {
            propertiesToEmit.etbId = markerOptions.etb;
            propertiesToEmit.osIds = markerOptions.os;
          }

          emit('onClickCard', a.originalEvent, propertiesToEmit);
        }
      });
    }

    markerGroup.on('click', (a) => {
      emitClickCard([a.layer], a.latlng, a);
    });

    markerGroup.on('clusterclick', (a) => {
      if (resursiveCheckNoGroups(mapSettings.map, a.layer)) {
        emitClickCard(a.layer.getAllChildMarkers(), a.latlng, a);
      } else {
        a.layer.zoomToBounds();
      }
    });

    let initZoomDone = false;
    function fitBoundsIfFirstInit(map) {
      if (!initZoomDone) {
        initZoomDone = true;
        map.fitBounds(markerGroup.getBounds());
      }
    }

    function success(position) {
      const userLocalisation = position.coords;
      const greenIcon = window.L.icon({
        iconUrl: '/themes/custom/santefr/images/icon-user-position-green.png',
        iconSize: [40, 45],
        iconAnchor: [45, 45],
        popupAnchor: [-3, -76],
      });
      const latLngUser = L.marker([userLocalisation.latitude, userLocalisation.longitude], { icon: greenIcon }).addTo(mapSettings.map);
      markerAroundMe.value = latLngUser;
      mapSettings.map.panTo(new L.LatLng(userLocalisation.latitude, userLocalisation.longitude));
      mapSettings.map.flyTo(latLngUser.getLatLng(), 15);
    }

    function error(err) {
      console.warn(`ERREUR (${err.code}): ${err.message}`);
    }

    function flyToBbox(bbox) {
      flyingOngoing = true;
      if (('geolocation' in navigator) && navigator.geolocation && props.whereValue === 'Autour de moi') {
        navigator.geolocation.getCurrentPosition(success, error);
      } else {
        const c1 = L.latLng(bbox.ne_lat, bbox.ne_lon);
        const c2 = L.latLng(bbox.so_lat, bbox.so_lon);
        const bounds = L.latLngBounds(c1, c2);
        mapSettings.map.fitBounds(bounds);
      }
    }

    function initZoom() {
      const zoomDefault = thematicCardService.getFilterDrupalSettings().zoom;
      initZoomDone = false;
      if (zoomDefault && zoomDefault.length === 2) {
        flyToBbox({
          ne_lat: zoomDefault[1][0],
          ne_lon: zoomDefault[1][1],
          so_lat: zoomDefault[0][0],
          so_lon: zoomDefault[0][1],
        });
        initZoomDone = true;
      }
    }

    // Update POIs on the map
    function drawPOI(map) {
      map.closePopup();
      emit('onCloseCard');
      map.removeLayer(markerGroup);
      markerGroup.clearLayers();
      props.cards.forEach((group) => {
        const nids = group.doclist.docs.reduce((nidsAcc, doc) => {
          nidsAcc.push(doc.its_nid);

          return nidsAcc;
        }, []);

        let existingCareDeals = false;
        group.doclist.docs.forEach((card) => {
          const myIcon = window.L.divIcon({
            className: 'leaflet-marker-icon marker-single-position',
            iconSize: null,
          });
          const optionsMarker = { nid: card.its_nid, icon: myIcon };

          if (card.ss_etb_item_id && card.ss_type === 'care_deals') {
            optionsMarker.etb = card.ss_etb_item_id;
            optionsMarker.os = nids;
          }

          const latLng = new L.LatLng(card.fts_field_geolocalisation_lat, card.fts_field_geolocalisation_lon);
          const marker = L.marker(latLng, optionsMarker);

          if (typeof card.ss_field_icon_type !== 'undefined') {
            const customIcon = marker.getIcon();
            customIcon.options.className += ` with-icon ${card.ss_field_icon_type}`;
            marker.setIcon(customIcon);
          }

          if (existingCareDeals === true) {
            return;
          }

          existingCareDeals = true;

          // Add marker to markers cluster
          markerGroup.addLayer(marker);
        });
      });
      map.addLayer(markerGroup);

      if (!personalizedZoom.value && (isRestModalFilter.value || props.whereValue === '')) {
        initZoom();
        isRestModalFilter.value = false;
      }

      if (props.cards && props.cards.length) {
        fitBoundsIfFirstInit(map);
      }
    }

    onMounted(() => {
      mapSettings = initMap({
        containerId: 'map-iframe',
        mapOptions: { zoomControl: false },
      });

      drawPOI(mapSettings.map);
      initZoom();

      mapSettings.map.on('moveend', () => {
        if (!flyingOngoing && initZoomDone) {
          emit('updateBoundingBox', { boundingBox: mapSettings.map.getBounds() });
        } else if (flyingOngoing) {
          flyingOngoing = false;
        }
      });
    });

    watch(() => props.cards, () => {
      drawPOI(mapSettings.map);
    });

    watch(() => props.modeType, () => {
      if (props.modeType === 'map') {
        nextTick(() => {
          mapSettings.map.invalidateSize();
        });
      }
    });

    function invalideSize() {
      flyingOngoing = true;

      nextTick(() => {
        mapSettings.map.invalidateSize();
      });
    }

    return {
      flyToBbox,
      invalideSize,
      isRestModalFilter,
      markerAroundMe,
    };
  },
};

</script>

<style lang="scss" scoped>
.text-in-area {
  position: absolute;
  transform: translate(-50%,-50%);
  top:50%;
  left: 50%;
  display: inline-block;
  color: #ffffff;
  background: #00457f;
  z-index: 1;
  padding: 15px 30px;
}
</style>
